import { useMemo, useState } from "react";
import { TableColumn } from "react-data-table-component";
import { Plus } from "react-feather";
import { Btn, H2, P } from "../../../../AbstractElements";
import CustomDataTable from "../../../../CommonElements/Table/CustomTable";
import { IMenusList } from "../../types";
import SkeletonTable from "../../../../CommonElements/Loader/SkeletonTableLoader";
import BadgeDetails from "../../../../CommonElements/Badge/BadgeDetails";
import { dateConversion } from "../../../../utils/dateUtils";
import { danger, success } from "../../../../utils/Constant";
import IconComponent from "../../../../CommonElements/IconComponent";
import TableSearch from "../../../../CommonElements/HeaderTableSearch";
import ActiveInactiveSwitch from "../../../../CommonElements/ActiveInactiveSwitch";

export interface IMenusListTableProps {
  list: IMenusList[];
  loading?: boolean;
  onAdd?: () => void;
  onAddUserMenu?: () => void;
  onEdit?: (menu: IMenusList) => void;
  onDelete?: (menu: IMenusList) => void;
  onStatusToggle?: (menu: IMenusList, value: boolean) => void;
}

interface IMenusTableRow extends IMenusList {
  level: number;
  parentName?: string;
}

type MenuChild = IMenusList | { data: IMenusList[] };
type MenuRowFilter = "all" | "parentMenus" | "parentRoutes" | "childRoutes";

const parentMenuHighlightColor = {
  background: "#eaf4ff",
  border: "#2f80ed",
  hover: "#dceeff",
};

const menuRowFilterOptions: Array<{ label: string; value: MenuRowFilter }> = [
  { label: "All Menus", value: "all" },
  { label: "Parent Menus", value: "parentMenus" },
  { label: "Parent Routes", value: "parentRoutes" },
  { label: "Child Routes", value: "childRoutes" },
];

const getChildren = (row: IMenusList): IMenusList[] => {
  const children = row.children as MenuChild[] | undefined;

  if (!Array.isArray(children)) return [];

  return children.flatMap((child) => {
    if ("data" in child && Array.isArray(child.data)) {
      return child.data;
    }

    return child as IMenusList;
  });
};

const flattenMenus = (
  menus: IMenusList[],
  level = 0,
  parentName?: string,
): IMenusTableRow[] =>
  menus.flatMap((menu) => {
    const children = getChildren(menu);
    const currentRow: IMenusTableRow = { ...menu, level, parentName };

    return [currentRow, ...flattenMenus(children, level + 1, menu.name)];
  });

const formatNumber = (value?: number | null) => value ?? "-";

const hasRoute = (row: IMenusTableRow) => Boolean(String(row.route ?? "").trim());

const hasParent = (row: IMenusTableRow) =>
  Boolean(row.parent_id) || row.level > 0 || Boolean(row.parentName);

const isParentMenuRow = (row: IMenusTableRow) =>
  !hasRoute(row) && !hasParent(row);

const isParentRouteRow = (row: IMenusTableRow) =>
  hasRoute(row) && !hasParent(row);

const isChildRouteRow = (row: IMenusTableRow) =>
  hasRoute(row) && hasParent(row);

const matchesMenuRowFilter = (row: IMenusTableRow, filter: MenuRowFilter) => {
  if (filter === "parentMenus") return isParentMenuRow(row);
  if (filter === "parentRoutes") return isParentRouteRow(row);
  if (filter === "childRoutes") return isChildRouteRow(row);

  return true;
};

const getSearchText = (row: IMenusTableRow): string =>
  [
    row.id,
    row.name,
    row.route,
    row.icon,
    row.parentName,
    row.parent_id,
    row.order,
    row.sort_order,
    row.is_default ? "yes default" : "no not default",
    row.status ? "active" : "inactive",
    dateConversion(row.created_at),
    dateConversion(row.updated_at),
  ]
    .filter((value) => value !== null && value !== undefined)
    .join(" ")
    .toLowerCase();

const MenusListTable = ({
  list,
  loading = false,
  onAdd,
  onAddUserMenu,
  onEdit,
  onDelete,
  onStatusToggle,
}: IMenusListTableProps) => {
  const [filterText, setFilterText] = useState("");
  const [rowFilter, setRowFilter] = useState<MenuRowFilter>("all");
  const tableData = useMemo(() => flattenMenus(list), [list]);
  const filteredData = useMemo(() => {
    const normalizedSearch = filterText.trim().toLowerCase();
    const rowFilterData = tableData.filter((row) =>
      matchesMenuRowFilter(row, rowFilter),
    );

    if (!normalizedSearch) return rowFilterData;

    return rowFilterData.filter((row) =>
      getSearchText(row).includes(normalizedSearch),
    );
  }, [filterText, rowFilter, tableData]);

  const subHeaderComponent = useMemo(
    () => (
      <div className="d-flex flex-wrap justify-content-start justify-content-lg-end align-items-center gap-2">
        <Btn
          className="m-0 f-14 btn-xs d-flex align-items-center"
          color="primary"
          onClick={onAdd}
        >
          <Plus size={14} className="me-1" /> Add Menu
        </Btn>
        <Btn
          className="m-0 f-14 btn-xs d-flex align-items-center"
          color="secondary"
          onClick={onAddUserMenu}
        >
          <Plus size={14} className="me-1" /> Add User Menu
        </Btn>
        <select
          className="form-select form-select-sm w-auto"
          value={rowFilter}
          onChange={(event) =>
            setRowFilter(event.target.value as MenuRowFilter)
          }
        >
          {menuRowFilterOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <TableSearch
          value={filterText}
          label="Search"
          containerClassName="dataTables_filter d-flex align-items-center"
          onChange={(event) => setFilterText(event.target.value)}
        />
      </div>
    ),
    [filterText, onAdd, onAddUserMenu, rowFilter],
  );

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: isParentMenuRow,
        style: {
          backgroundColor: parentMenuHighlightColor.background,
          borderLeft: `4px solid ${parentMenuHighlightColor.border}`,
          color: "#101828",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: parentMenuHighlightColor.hover,
          },
        },
      },
    ],
    [],
  );

  const columns: TableColumn<IMenusTableRow>[] = [
    {
      name: "#",
      cell: (_, index) => <P className="m-0">{index + 1}</P>,
      width: "60px",
    },
    {
      name: "Name",
      cell: (row) => (
        <div style={{ paddingLeft: row.level * 16 }}>
          <P className="m-0">{row.name}</P>
        </div>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: "Route",
      cell: (row) => <P className="m-0">{row.route || "-"}</P>,
      sortable: true,
      width: "250px",
    },
    {
      name: "Parent",
      cell: (row) => (
        <P className="m-0">{(row.parent?.name) || "-"}</P>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: "Order",
      cell: (row) => <P className="m-0">{formatNumber(row.sort_order)}</P>,
      sortable: true,
      width: "100px",
    },
    {
      name: "Default",
      cell: (row) => (
        <BadgeDetails
          color={row.is_default ? success : danger}
          text={row.is_default ? "Yes" : "No"}
        />
      ),
      sortable: true,
      width: "100px",
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex align-items-center gap-3">
          <ActiveInactiveSwitch
            isShowLabel={false}
            isActive={row.status === 1}
            className="switch-container_second"
            onToggle={(value) => onStatusToggle?.(row, value)}
          />
          <IconComponent
            className="fa fa-edit f-16 text-primary pointer"
            onClick={() => onEdit?.(row)}
          />
          <IconComponent
            className="icofont icofont-ui-delete text-danger f-16 pointer"
            onClick={() => onDelete?.(row)}
          />
        </div>
      ),
      width: "150px",
    },
  ];
  return (
    <>
      <H2 className="mb-1 f-16">Menus</H2>
      <div className="table-responsive user-datatable custom-scrollbar d-none d-lg-block">
        <CustomDataTable
          columns={columns}
          data={filteredData}
          loading={loading}
          conditionalRowStyles={conditionalRowStyles}
          subHeader
          subHeaderComponent={subHeaderComponent}
          loaderComponent={
            <SkeletonTable
              rows={filteredData.length || 8}
              columns={columns}
              rowHeight={40}
              gap={4}
            />
          }
        />
      </div>
    </>
  );
};

export default MenusListTable;
